/* eslint-disable @typescript-eslint/no-explicit-any */
import { BoolLiteral, Literal, NumberLiteral, StringLiteral, Identifier, Expression, Factor, DefaultValue, Pipeline, Call, Cases, CaseItem, CodePos, NullLiteral, CaseBody, ArrayLiteral, ObjectLiteral, Or, MaySpreadFactor, BinaryExpr } from './ast'
import { Lexer, Token, TokenKind } from './lexer'

export type ParseResult<T> = ParseOk<T> | ParseErr

export type ParseOk<T> = {
  value: T
  tailLexer: Lexer
}

export type UnExpectedToken = { token: Token }
export type EndOfInput = 'EndOfInput'
export type ParseErr = {
  reason: UnExpectedToken | EndOfInput | string
}

export function isParseOk<T>(res: ParseResult<T>): res is ParseOk<T> {
  return (<ParseOk<T>>res).value !== undefined
}

export function isParseErr<T>(res: ParseResult<T>): res is ParseErr {
  return !isParseOk(res)
}
export const parseOk = <T>(value: T, tailLexer: Lexer): ParseOk<T> => ({ value, tailLexer })
export const parseErr = (reason: UnExpectedToken | EndOfInput): ParseErr => ({ reason })

type ParseFn<T> = (lexer: Lexer) => ParseResult<T>

// Generates a parseFn to match the specified token
function signalToken(tokenKind: TokenKind): ParseFn<Token> {
  return lexer => {
    const lexerClone = lexer.clone()
    const { done, value } = lexerClone.next()
    if (done || !value) {
      return parseErr('EndOfInput')
    }
    if (value.kind != tokenKind) {
      return parseErr({ token: value })
    }
    return parseOk(value, lexerClone)
  }
}

// Returns ParseErr if the option is ParseErr, otherwise calls f with the wrapped value and returns the result.
function map<T1, T2>(f: ParseFn<T1>, op: (ok: ParseOk<T1>) => ParseResult<T2>): ParseFn<T2> {
  return lexer => {
    const res = f(lexer)
    if (isParseOk(res)) {
      return op(res)
    }
    return res
  }
}

type ParseFns<T> = T extends [infer L, ...infer R]
  ? [ParseFn<L>, ...ParseFns<R>]
  : T extends []
  ? T
  : [ParseFn<T>]

// Applies a tuple of parsers one by one and returns their results as a tuple.
function all<T extends any[]>(...fns: ParseFns<T>): ParseFn<T> {
  return allInner(...fns) as ParseFn<T>
}

const allInner: (...fns: ParseFn<any>[]) => ParseFn<any[]> =
  (fn, ...fns) => (lexer: Lexer) => {
    if (!fn) {
      return parseOk([], lexer)
    }
    const fnRes = fn(lexer)
    if (isParseOk(fnRes)) {
      const tailFnRes = allInner(...fns)(fnRes.tailLexer)
      if (isParseOk(tailFnRes)) {
        return parseOk([fnRes.value, ...tailFnRes.value], tailFnRes.tailLexer)
      }
      return tailFnRes
    }
    return fnRes
  }


// TupleToUnion<['1', '2', '3']>  === '1' | '2' | '3'
type TupleToUnion<T extends any[]> =
  T extends [infer L, ...infer R]
  ? L | TupleToUnion<R>
  : never


// Recognizes one of the provided ParserFn.
function oneOf<T extends any[]>(...fns: ParseFns<T>): ParseFn<TupleToUnion<T>> {
  return oneOfInner(...fns) as ParseFn<TupleToUnion<T>>
}

const oneOfInner: (...fns: ParseFns<any>[]) => ParseFn<any> =
  (fn, ...fns) => (lexer: Lexer) => {
    const fnRes = fn(lexer)
    if (fns.length === 0) {
      // fn is the last func
      return fnRes
    }
    return isParseOk(fnRes) ? fnRes : oneOfInner(...fns)(lexer)
  }

// Optional parser: Will return `null` if not successful.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const opt: <T>(f: ParseFn<T>) => ParseFn<T | null> =
  f => (lexer: Lexer) => {
    const fRes = f(lexer)
    if (isParseOk(fRes)) {
      return fRes
    }
    return parseOk(null, lexer)
  }


// Runs the parseFn until it fails and returns the results in a array.
// Fails if the ParseFn does not produce at least one result.
const many1: <T>(f: ParseFn<T>) => ParseFn<T[]> =
  f => (lexer: Lexer) => {
    const parseRes = []
    let tailLexer = lexer
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const fRes = f(tailLexer)
      if (isParseOk(fRes)) {
        parseRes.push(fRes.value)
        tailLexer = fRes.tailLexer
      } else {
        if (parseRes.length === 0) {
          return fRes
        }
        break
      }
    }
    return parseOk(parseRes, tailLexer)
  }

// Runs the parseFn until it fails and returns the results in a array.
const many0: <T>(f: ParseFn<T>) => ParseFn<T[]> =
  f => (lexer: Lexer) => {
    const fRes = many1(f)(lexer)
    if (isParseErr(fRes)) {
      return parseOk([], lexer)
    }
    return fRes
  }

export const numberLiteral: ParseFn<NumberLiteral> = map<Token, NumberLiteral>(signalToken('NumberLiteral'), ({ value, tailLexer }) => ({
  value: {
    type: 'NumberLiteral',
    value: Number(value.value),
    codePos: value.codePos
  },
  tailLexer
}))

export const stringLiteral: ParseFn<StringLiteral> = map<Token, StringLiteral>(signalToken('StringLiteral'), ({ value, tailLexer }) => ({
  value: {
    type: 'StringLiteral',
    value: value.value.substring(1, value.value.length - 1),
    codePos: value.codePos
  },
  tailLexer
}))

export const boolLiteral: ParseFn<BoolLiteral> = map<Token, BoolLiteral>(signalToken('BoolLiteral'), ({ value, tailLexer }) => ({
  value: {
    type: 'BoolLiteral',
    value: value.value === 'true',
    codePos: value.codePos
  },
  tailLexer
}))

export const nullLiteral: ParseFn<NullLiteral> = map<Token, NullLiteral>(signalToken('NullLiteral'), ({ value, tailLexer }) => ({
  value: {
    type: 'NullLiteral',
    value: null,
    codePos: value.codePos
  },
  tailLexer
}))

// [1,2,3,null,'1']
export const arrayLiteral: ParseFn<ArrayLiteral> =
  map(all<[Token, [MaySpreadFactor, Token][], MaySpreadFactor | null, Token]>(
    signalToken('ArrayOpenTag'),
    many0(all<[MaySpreadFactor, Token]>(maySpreadFactor, signalToken('Comma'))),
    opt(maySpreadFactor),
    signalToken('ArrayCloseTag'),
  ), ({ value, tailLexer }) => {
    const [openTag, arr, signalItem, closeTag] = value
    const array = arr.map(item => item[0])
    if (signalItem) {
      array.push(signalItem)
    }
    return {
      value: {
        type: 'ArrayLiteral',
        value: array,
        ...codePos(openTag, closeTag)
      },
      tailLexer
    }
  })

// {'a': 1, 'b': null}
export const objectLiteral: ParseFn<ObjectLiteral> =
  map(all<[Token, [StringLiteral, Token, Factor, Token][], [StringLiteral, Token, Factor] | null, Token]>(
    signalToken('ObjectOpenTag'),
    many0(all<[StringLiteral, Token, Factor, Token]>(
      stringLiteral,
      signalToken('Colon'),
      factor,
      signalToken('Comma')
    )),
    opt(all<[StringLiteral, Token, Factor]>(
      stringLiteral,
      signalToken('Colon'),
      factor,
    )),
    signalToken('CloseTag')
  ), ({ value, tailLexer }) => {
    const [openTag, kvs, kv, closeTag] = value
    const obj = kvs.reduce((prev, cur) => {
      prev[cur[0].value] = cur[2]
      return prev
    }, {} as ObjectLiteral['value'])
    if (kv) {
      obj[kv[0].value] = kv[2]
    }
    return {
      value: {
        type: 'ObjectLiteral',
        value: obj,
        ...codePos(openTag, closeTag)
      },
      tailLexer
    }
  })

export const literal: ParseFn<Literal> = oneOf<[BoolLiteral, StringLiteral, NumberLiteral, NullLiteral, ArrayLiteral, ObjectLiteral]>(boolLiteral, stringLiteral, numberLiteral, nullLiteral, arrayLiteral, objectLiteral)

export const identifier = map<Token, Identifier>(signalToken('Identifier'), ({ value, tailLexer }) => ({
  value: {
    type: 'Identifier',
    name: value.value,
    codePos: value.codePos
  },
  tailLexer
}))

// func arg1 arg2 arg3 ...
export function call(lexer: Lexer): ParseResult<Call> {
  return map(all<[Identifier, Factor[]]>(
    identifier,
    many0(factor)
  ), ok => ({
    value: {
      type: 'Call',
      func: ok.value[0].name,
      args: ok.value[1],
      ...codePos(ok.value[0], ...ok.value[1])
    } as Call,
    tailLexer: ok.tailLexer
  }))(lexer)
}

// a + '123'
export function binaryExpr(lexer: Lexer): ParseResult<BinaryExpr> {

  type FactorWithoutBinaryExpr = Literal | Identifier
  const factorWithoutBinaryExpr = oneOf<[Literal, Identifier]>(
    literal,
    identifier,
  )
  return map(all<[FactorWithoutBinaryExpr, [Token, FactorWithoutBinaryExpr][]]>(
    factorWithoutBinaryExpr,
    many1(
      all<[Token, FactorWithoutBinaryExpr]>(signalToken('Add'), factorWithoutBinaryExpr)
    ),
  ), ({ value, tailLexer }) => {
    function composeBinaryExpr(
      factWithoutBin: FactorWithoutBinaryExpr,
      opAndFactorArr: [Token, FactorWithoutBinaryExpr][]
    ): BinaryExpr {
      const [rightExpr, ...tail] = opAndFactorArr
      let right
      if (tail.length === 0) {
        right = rightExpr[1]
      } else {
        right = composeBinaryExpr(rightExpr[1], tail)
      }
      return {
        type: 'BinaryExpr',
        op: rightExpr[0].value,
        left: factWithoutBin,
        right,
        ...codePos(factWithoutBin, ...opAndFactorArr.map(x => x[1]))
      } as BinaryExpr
    }
    const [factWithoutBin, opAndFactorArr] = value

    return {
      value: composeBinaryExpr(factWithoutBin, opAndFactorArr),
      tailLexer
    }
  })(lexer)
}

// a
// 'str'
// 123
// true
// []
// {}
export function factor(lexer: Lexer): ParseResult<Factor> {
  return oneOf<[BinaryExpr, Literal, Identifier]>(
    binaryExpr,
    literal,
    identifier
  )(lexer)
}

// ...[1,2,3]
// ...a
// ...{'a': 1}
// factor
export function maySpreadFactor(lexer: Lexer): ParseResult<MaySpreadFactor> {
  return map(
    all<[Token | null, Factor]>(opt(signalToken('Spread')), factor),
    ({ value, tailLexer }) => {
      const [spread, fact] = value
      let cp = fact.codePos
      if (spread) {
        cp = codePos(spread, fact).codePos
      }
      return {
        value: {
          type: 'MaySpreadFactor',
          hasSpread: spread !== null,
          value: fact,
          codePos: cp
        } as MaySpreadFactor,
        tailLexer
      }
    }
  )(lexer)
}

// case 'xx' xx
export function caseItem(lexer: Lexer): ParseResult<CaseItem> {
  return map(all<[Token, Or, Factor]>
    (signalToken('Case'), or, factor),
    ok => ({
      value: {
        type: 'CaseItem',
        test: ok.value[1],
        value: ok.value[2],
        ...codePos(...ok.value)
      } as CaseItem,
      tailLexer: ok.tailLexer
    }))(lexer)
}

// default null
export function caseDefault(lexer: Lexer): ParseResult<Factor> {
  return map(all<[Token, Factor]>(signalToken('Default'), factor), ok => ({
    value: {
      ...ok.value[1],
      ...codePos(...ok.value)
    },
    tailLexer: ok.tailLexer
  }))(lexer)
}

// a or 'b' or 1 or true or null
export function or(lexer: Lexer): ParseResult<Or> {
  return map(all<[[Factor, Token][], Factor | null]>(
    many0(all<[Factor, Token]>(factor, signalToken('Or'))),
    opt(factor)
  ), ({ value, tailLexer }) => {
    const [arr, signalItem] = value
    const array = arr.map(item => item[0])
    if (signalItem) {
      array.push(signalItem)
    }
    if (array.length === 0) {
      return { reason: 'No match to or expression' } as ParseErr
    }
    return {
      value: {
        type: 'Or',
        value: array,
        ...codePos(...array)
      } as Or,
      tailLexer
    }
  })(lexer)
}

// | case 'xx' xx | case 'yy' yy | default null
export function caseBody(lexer: Lexer): ParseResult<CaseBody> {
  return map(
    all<[[Token, CaseItem][], [Token, Factor]]>(
      many1(all<[Token, CaseItem]>(signalToken('Bar'), caseItem)),
      all<[Token, Factor]>(signalToken('Bar'), caseDefault)
    ), ok => ({
      value: {
        type: 'CaseBody',
        cases: ok.value[0].map(item => item[1]),
        default: ok.value[1][1],
        ...codePos(ok.value[0][0][0], ok.value[1][1])
      } as CaseBody,
      tailLexer: ok.tailLexer
    }))(lexer)
}

// | toString | replace '-' '_'
export function pipelineChains(lexer: Lexer): ParseResult<Call[]> {
  return map(many1(all<[Token, Call]>(signalToken('Bar'), call)), ok => ({
    value: ok.value.map(v => ({
      ...v[1],
      ...codePos(...v)
    })),
    tailLexer: ok.tailLexer
  }))(lexer)
}

// , xx
export function defaultValue(lexer: Lexer): ParseResult<Factor> {
  return map(all<[Token, Factor]>(signalToken('Comma'), factor), ok => ({
    value: {
      ...ok.value[1],
      ...codePos(...ok.value)
    },
    tailLexer: ok.tailLexer
  }))(lexer)
}

export function expression(lexer: Lexer): ParseResult<Expression> {
  function isCaseBody(v: any): v is CaseBody {
    return (v as CaseBody).type === 'CaseBody'
  }

  return map(
    all<[[Factor, Factor | null], (CaseBody | Call[])[], Factor | null]>(
      all<[Factor, Factor | null]>(factor, opt(defaultValue)),
      many0(
        oneOf<[CaseBody, Call[]]>(
          caseBody,
          pipelineChains,
        )
      ),
      // defaultValue
      opt(defaultValue)
    ), ok => {
      const [[factor, firstDefaultValue], exprs, lastDefaultValue] = ok.value
      let firstExpr: Expression = factor
      if (firstDefaultValue) {
        firstExpr = {
          type: 'DefaultValue',
          expr: factor,
          value: firstDefaultValue,
          ...codePos(factor, firstDefaultValue)
        } as DefaultValue
      }
      let expr = exprs.reduce<Expression>((acc, next) => {
        if (isCaseBody(next)) {
          return {
            type: 'Cases',
            discriminant: acc,
            caseBody: next,
            ...codePos(acc, next)
          } as Cases
        } else {
          return {
            type: 'Pipeline',
            input: acc,
            chains: next,
            ...codePos(acc, ...next)
          } as Pipeline
        }
      }, firstExpr)

      if (lastDefaultValue) {
        expr = {
          type: 'DefaultValue',
          expr,
          value: lastDefaultValue,
          ...codePos(expr, lastDefaultValue)
        } as DefaultValue
      }
      return {
        value: expr,
        tailLexer: ok.tailLexer
      }
    }
  )(lexer)
}

export function statement(lexer: Lexer): ParseResult<Expression> {
  return map(all<[Token, Expression, Token]>(
    signalToken('OpenTag'),
    expression,
    signalToken('CloseTag'),
  ), ok => ({
    value: ok.value[1],
    tailLexer: ok.tailLexer
  }))(lexer)
}

function codePos(...cps: CodePos[]): CodePos {
  return {
    codePos: [cps[0].codePos[0], cps[cps.length - 1].codePos[1]]
  }
}
